import React, { useState } from 'react';
import Button from '../common/Button';
import { FiSave, FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

/**
 * ContentEditor Component
 * Content editing UI mockup (no actual data persistence)
 */
const ContentEditor = ({ category }) => {
  // Mock form data based on category
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    description: '',
    descriptionEn: '',
    imageUrl: '',
    specs: [{ label: '', labelEn: '', value: '', valueEn: '' }]
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Add specification field
  const addSpecField = () => {
    setFormData((prev) => ({
      ...prev,
      specs: [...prev.specs, { label: '', labelEn: '', value: '', valueEn: '' }]
    }));
  };

  // Remove specification field
  const removeSpecField = (index) => {
    setFormData((prev) => ({
      ...prev,
      specs: prev.specs.filter((_, i) => i !== index)
    }));
  };

  // Handle specification change
  const handleSpecChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === index ? { ...spec, [field]: value } : spec
      )
    }));
  };

  const handleSave = () => {
    alert('저장되었습니다! (UI 목업 - 실제 저장 기능 없음)');
  };

  const handleCancel = () => {
    if (window.confirm('변경사항을 취소하시겠습니까?')) {
      setFormData({
        name: '',
        nameEn: '',
        description: '',
        descriptionEn: '',
        imageUrl: '',
        specs: [{ label: '', labelEn: '', value: '', valueEn: '' }]
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Editor Header */}
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {category === 'products' && '제품 관리'}
          {category === 'company' && '회사 소개 편집'}
          {category === 'contact' && '연락처 수정'}
          {category === 'images' && '이미지 URL 변경'}
        </h2>
        <p className="text-gray-600 mt-1">
          콘텐츠를 편집하고 저장하세요 (UI 목업)
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Product/Content Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              이름 (한글) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="제품명 또는 콘텐츠명"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Name (English) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              placeholder="Product or Content Name"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            />
          </div>
        </div>

        {/* Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              설명 (한글)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="상세 설명을 입력하세요"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
            ></textarea>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description (English)
            </label>
            <textarea
              name="descriptionEn"
              value={formData.descriptionEn}
              onChange={handleChange}
              rows="4"
              placeholder="Enter detailed description"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
            ></textarea>
          </div>
        </div>

        {/* Image URL */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            이미지 URL
          </label>
          <input
            type="text"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          />
        </div>

        {/* Dynamic Specification Fields */}
        {category === 'products' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-semibold text-gray-700">
                기술 사양
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSpecField}
                className="flex items-center space-x-1"
              >
                <FiPlus className="w-4 h-4" />
                <span>항목 추가</span>
              </Button>
            </div>

            <div className="space-y-4">
              {formData.specs.map((spec, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-md border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={spec.label}
                      onChange={(e) => handleSpecChange(index, 'label', e.target.value)}
                      placeholder="라벨 (한글)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                    />
                    <input
                      type="text"
                      value={spec.labelEn}
                      onChange={(e) => handleSpecChange(index, 'labelEn', e.target.value)}
                      placeholder="Label (English)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                    />
                    <input
                      type="text"
                      value={spec.value}
                      onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                      placeholder="값 (한글)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                    />
                    <input
                      type="text"
                      value={spec.valueEn}
                      onChange={(e) => handleSpecChange(index, 'valueEn', e.target.value)}
                      placeholder="Value (English)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                    />
                  </div>
                  {formData.specs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSpecField(index)}
                      className="mt-2 text-red-600 hover:text-red-700 text-sm flex items-center space-x-1"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      <span>항목 삭제</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          className="flex items-center space-x-2"
        >
          <FiX className="w-4 h-4" />
          <span>취소</span>
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          className="flex items-center space-x-2"
        >
          <FiSave className="w-4 h-4" />
          <span>저장</span>
        </Button>
      </div>

      {/* Info Notice */}
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-sm text-yellow-800">
          <strong>알림:</strong> 이 페이지는 UI 목업입니다. 실제 데이터 저장 및 수정 기능은 구현되지 않았습니다.
        </p>
      </div>
    </div>
  );
};

export default ContentEditor;
